import React, { useState, useRef, useEffect, useCallback } from "react";
import Header from "../../components/shared/Header";
import AboutUs from "./AboutUs";
import Hero from "./Hero";
import OurFeatures from "./OurFeatures";
import Roadmap from "./Roadmap";
import Footer from "../../components/shared/Footer";

import ScrollHijackContainer from "../../components/ScrollHijackContainer";
import Loader from "../../components/shared/Loader";

//! Preloading assets
/* Images and videos to preload */
// Hero
import hero_character from "../../assets/videos/hero-character.webm";
import hero_character_mov from "../../assets/videos/hero-character.mov";

import ImgLightHero from "../../assets/images/light-hero.svg";
import ImgHeroCloud1 from "../../assets/images/hero-cloud-1.svg";
import ImgHeroCloud2 from "../../assets/images/hero-cloud-2.svg";
import ImgMagicIcon from "../../assets/images/magic-icon.png";
import ImgP2PIcon from "../../assets/images/p2p-icon.png";
import ImgShieldIcon from "../../assets/images/shield-icon.png";
import ImgChatIcon from "../../assets/images/chat-icon.png";

// Our Features
import ImgJaggedLine from "../../assets/images/jagged-line.svg";
import ImgMixerThunder from "../../assets/images/mixer-thunder.svg";
import ImgMixerPipe from "../../assets/images/mixer-pipe.png";
import ImgMixerCoin from "../../assets/images/mixer-coin.png";
import ImgLightMixer from "../../assets/images/light-mixer.svg";
import ImgPromptOn from "../../assets/images/prompt-on.png";
import ImgPromptOff from "../../assets/images/prompt-off.png";
import ImgPromptCloud1 from "../../assets/images/prompt-cloud-1.svg";
import ImgPromptCloud2 from "../../assets/images/prompt-cloud-2.svg";
import ImgLightPromptify from "../../assets/images/light-promptify.svg";

// About Us
import ImgAboutUsCloud from "../../assets/images/about-us-cloud.svg";
import ImgStormCharacter from "../../assets/images/storm-character.png";
import ImgLightMcp from "../../assets/images/light-mcp.svg";
import ImgGears from "../../assets/images/gears.png";
import ImgMcpLines from "../../assets/images/mcp-lines.png";

const preloadImage = (src) => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.src = src;
		img.onload = resolve;
		img.onerror = reject;
	});
};

// --- Helper function to preload a video ---
const preloadVideo = (src) => {
	return new Promise((resolve, reject) => {
		const video = document.createElement("video");
		// Required for autoplay and preloading on iOS
		video.playsInline = true;
		video.muted = true;
		video.autoplay = true;
		video.src = src;
		// The 'canplaythrough' event is a good indicator that the video has buffered enough to play without stopping.
		video.oncanplaythrough = resolve;
		video.onerror = reject;
	});
};

export default function Home() {
	const [isLoading, setIsLoading] = useState(true);

	const [lockPoint, setLockPoint] = useState(99999); // Start high to avoid premature lock
	const [isLocked, setIsLocked] = useState(false);
	const [lockScrollUp, setLockScrollUp] = useState(false);
	const [lockScrollDown, setLockScrollDown] = useState(true);
	const [position, setPosition] = useState(0);

	const roadmapRef = useRef(null);

	const onLockStatusChange = useCallback((locked) => {
		if (locked) {
			setTimeout(() => setIsLocked(true), 800);
		} else {
			setIsLocked(false);
		}
	}, []);

	useEffect(() => {
		// ! Preloading assets
		const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(
			navigator.userAgent
		);

		const preloadAssets = async () => {
			try {
				const imagePromises = [
					preloadImage(ImgLightHero),
					preloadImage(ImgHeroCloud1),
					preloadImage(ImgHeroCloud2),
					preloadImage(ImgMagicIcon),
					preloadImage(ImgP2PIcon),
					preloadImage(ImgShieldIcon),
					preloadImage(ImgChatIcon),
					preloadImage(ImgJaggedLine),
					preloadImage(ImgMixerThunder),
					preloadImage(ImgMixerPipe),
					preloadImage(ImgMixerCoin),
					preloadImage(ImgLightMixer),
					preloadImage(ImgPromptOn),
					preloadImage(ImgPromptOff),
					preloadImage(ImgPromptCloud1),
					preloadImage(ImgPromptCloud2),
					preloadImage(ImgLightPromptify),
					preloadImage(ImgAboutUsCloud),
					preloadImage(ImgStormCharacter),
					preloadImage(ImgLightMcp),
					preloadImage(ImgGears),
					preloadImage(ImgMcpLines),
				];

				// Preload videos based on browser type
				const videoPromises = isSafariBrowser
					? [preloadVideo(hero_character_mov)]
					: [preloadVideo(hero_character)];

				// You can also preload fonts, data from an API, etc. here
				// Use document.fonts.ready which returns a promise that resolves when all fonts are loaded.
				const fontPromise = document.fonts.ready;
				// const dataPromise = fetch('https://api.example.com/data');

				// Combine all promises into one
				await Promise.all([
					...imagePromises,
					...videoPromises,
					fontPromise,
				]);

				// All assets are preloaded, hide the loader
				setIsLoading(false);
			} catch (error) {
				console.error("Failed to preload assets", error);
				// You might want to still hide the loader and show the page,
				// even if some assets failed to load.
				setIsLoading(false);
			}
		};

		preloadAssets();

		const calculateLockPoint = () => {
			if (roadmapRef.current) {
				// setLockPoint will be the top of the Roadmap component
				const roadmapLayoutBottom =
					roadmapRef.current.querySelector(".roadmap-layout")
						.offsetTop +
					roadmapRef.current.querySelector(".roadmap-layout")
						.offsetHeight;

				// lockpoint = when bottom of the Roadmap Layout reached 90% of the viewport height
				setLockPoint(roadmapLayoutBottom - window.innerHeight * 0.9);
			}
		};

		// Calculate on initial load
		calculateLockPoint();

		// Calculate lock point every 500ms
		const calculateLockPointInterval = setInterval(calculateLockPoint, 500);

		// Recalculate on window resize
		window.addEventListener("resize", calculateLockPoint);

		// Cleanup the event listener
		return () => {
			clearInterval(calculateLockPointInterval);
			window.removeEventListener("resize", calculateLockPoint);
		};
	}, []); // Empty dependency array means this runs once on mount

	return (
		<>
			<ScrollHijackContainer
				lockPoint={lockPoint}
				onPositionChange={setPosition}
				onLockStatusChange={setIsLocked}
				lockScrollUp={lockScrollUp}
				lockScrollDown={lockScrollDown}
				isLoading={isLoading}
			>
				<Loader loading={isLoading} />

				<Header />

				<Hero />

				<OurFeatures position={position} />

				<AboutUs position={position} />

				<Roadmap
					ref={roadmapRef}
					isLocked={isLocked}
					setLockScrollUp={setLockScrollUp}
					setLockScrollDown={setLockScrollDown}
				/>

				<Footer />

				{/* Uncomment for debugging lock point */}
				{/* <div
					className="debug-element"
					style={{
						width: "100%",
						height: "2px",
						backgroundColor: "rgba(255, 0, 0, 0.5)",
						position: "absolute",
						top: `${lockPoint}px`,
						left: "0",
						zIndex: "1000",
					}}
				></div> */}
			</ScrollHijackContainer>
		</>
	);
}
